import {
  Box,
  Button,
  Card,
  IconButton,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { Calendar, Views, dayjsLocalizer } from "react-big-calendar";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "react-big-calendar/lib/addons/dragAndDrop/styles.css";
import withDragAndDrop from "react-big-calendar/lib/addons/dragAndDrop";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore"; // ES 2015
import isSameOrAfter from "dayjs/plugin/isSameOrAfter"; // ES 2015

import React, { useCallback, useContext, useEffect, useState } from "react";
import CalendarToolBar from "./CalendarToolBar";
import { useTheme } from "@emotion/react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AuthContext } from "../../../../context/AuthContext";
import QueryString from "query-string";
import { Add } from "@mui/icons-material";
import ShopCalendarEvent from "./ShopCalendarEvent";
import ScheduleButton from "./ScheduleButton";
import "./ShopCalendar.css";
import CustomWeekView from "./CustomWeekView";
import CustomMonthView from "./CustomMonthView";
import EditJobDialog from "./EditJobDialog";
import JobDialog from "./JobDialog";
import useSchedule from "../../../../hooks/useSchedule";
import DeleteJobDialog from "./DeleteJobDialog";
import { SnackAlertContext } from "../../../../context/SnackAlertContext";

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc"; // ES 2015
import timezone from "dayjs/plugin/timezone"; // ES 2015
import isoWeek from "dayjs/plugin/isoWeek"; // ES 2015
import ScheduleJobDialog from "./ScheduleJobDialog";
import SelectMultipleShopEmployees from "./SelectMultipleShopEmployees";
import SelectWorkStation from "./SelectWorkStation";
import { View } from "@react-pdf/renderer";
import { getWorkStationColor } from "../../../../helper/workStationHelper";
import ScheduleJobEventsDialog from "./ScheduleJobEventsDialog";

const DnDCalendar = withDragAndDrop(Calendar);
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isoWeek);
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

const DEFAULT_TIMEZONE = "America/Phoenix";
dayjs.tz.setDefault(DEFAULT_TIMEZONE);
const localizer = dayjsLocalizer(dayjs);

const DEFAULT_SCHEDULE_JOB_DIALOG_DATA = {
  open: false,
  date: null,
  calendarEvent: null,
  isReScheduling: false,
};

const DEFAULT_EDIT_JOB_DIALOG_DATA = {
  open: false,
  date: null,
  calendarEvent: null,
};

const DEFAULT_DELETE_JOB_DIALOG_DATA = {
  open: false,
  date: null,
  calendarEvent: null,
};

const DEFAULT_JOB_DIALOG_DATA = {
  open: false,
  date: null,
  calendarEvent: null,
};

const NAVBAR_HEIGHT = 64;
const PADDING_TOP = 8;
const ShopCalendar = (props) => {
  // Calculate Screen Height with UseEffect
  const [calendarHeight, setCalendarHeight] = useState(0);
  const handleResize = () => {
    // calendar height = height of screen - navbar height
    setCalendarHeight(window.innerHeight - NAVBAR_HEIGHT - PADDING_TOP);
  };

  const { openSnackMessage } = useContext(SnackAlertContext);
  const { authToken, ezorder, decoded } = useContext(AuthContext);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { scheduleEvents, scheduleEvent, updateEvent, deleteEvent } =
    useSchedule();
  const isoDateParam = searchParams.get("date");
  const viewParam = searchParams.get("view");
  const entityParam = searchParams.get("entity");
  const assignedToParam = searchParams.get("assignedTo");
  const workStationParam = searchParams.get("workStation");

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const isMediumScreen = useMediaQuery(theme.breakpoints.down("md"));
  const isLargeScreen = useMediaQuery(theme.breakpoints.down("lg"));

  const [date, setDate] = useState();
  const [view, setView] = useState();

  const [calendarEvents, setCalendarEvents] = useState([]);

  const [scheduleJobDialogData, setScheduleJobDialogData] = useState(
    DEFAULT_SCHEDULE_JOB_DIALOG_DATA,
  );

  const [editJobDialogData, setEditJobDialogData] = useState(
    DEFAULT_EDIT_JOB_DIALOG_DATA,
  );

  const [deleteJobDialogData, setDeleteJobDialogData] = useState(
    DEFAULT_DELETE_JOB_DIALOG_DATA,
  );

  const [jobDialogData, setJobDialogData] = useState(DEFAULT_JOB_DIALOG_DATA);

  const [maxCellHeight, setMaxCellHeight] = useState();

  // Go to a specific date and change to day view
  const handleDaySelect = (date) => {
    console.log("handleDaySelect", date);
    navigate(
      `/shop-schedule?date=${dayjs(date)
        .tz(DEFAULT_TIMEZONE)
        .startOf("day")
        .format("YYYY-MM-DD")}&view=${"day"}&entity=${
        entityParam ?? ""
      }&assignedTo=${assignedToParam ?? ""}&workStation=${
        workStationParam ?? ""
      }`,
    );
  };

  const handleOnChangeView = (view) => {
    navigate(
      `/shop-schedule?date=${
        isoDateParam
          ? isoDateParam
          : dayjs().tz(DEFAULT_TIMEZONE).startOf("day").format("YYYY-MM-DD")
      }&view=${view}&entity=${entityParam ?? ""}&assignedTo=${
        assignedToParam ?? ""
      }&workStation=${workStationParam ?? ""}`,
    );
  };

  const handleEntityFilter = (entity) => {
    navigate(
      `/shop-schedule?date=${
        isoDateParam
          ? isoDateParam
          : dayjs().tz(DEFAULT_TIMEZONE).startOf("d").format("YYYY-MM-DD")
      }&view=${view}&entity=${entity ?? ""}&assignedTo=${
        assignedToParam ?? ""
      }&workStation=${workStationParam ?? ""}`,
    );
  };

  const handleAssignedToFilter = (assignedTo) => {
    navigate(
      `/shop-schedule?date=${
        isoDateParam
          ? isoDateParam
          : dayjs().tz(DEFAULT_TIMEZONE).startOf("d").format("YYYY-MM-DD")
      }&view=${view}&entity=${entityParam ?? ""}&assignedTo=${assignedTo.join(
        ",",
      )}&workStation=${workStationParam ?? ""}`,
    );
  };
  const handleWorkStationFilter = (workStation) => {
    navigate(
      `/shop-schedule?date=${
        isoDateParam
          ? isoDateParam
          : dayjs().tz(DEFAULT_TIMEZONE).startOf("d").format("YYYY-MM-DD")
      }&view=${view}&entity=${entityParam ?? ""}&assignedTo=${
        assignedToParam ?? ""
      }&workStation=${workStation ? workStation.id : ""}`,
    );
  };

  const onNavigate = useCallback(
    (newDate, fromCalendarView) => {
      let dateString = dayjs(newDate)
        .tz(DEFAULT_TIMEZONE)
        .startOf("d")
        .format("YYYY-MM-DD");
      console.log("ON NAVIGATE", dateString, fromCalendarView);
      navigate(
        `/shop-schedule?date=${dateString}&view=${
          fromCalendarView == "month" ||
          fromCalendarView == "linear_month" ||
          fromCalendarView == "week"
            ? Views.DAY
            : viewParam
              ? viewParam
              : isSmallScreen
                ? Views.DAY
                : Views.WEEK
        }&entity=${entityParam ?? ""}&assignedTo=${
          assignedToParam ?? ""
        }&workStation=${workStationParam ?? ""}`,
      );
    },
    [viewParam, entityParam, assignedToParam, workStationParam, navigate],
  );

  const onEventResize = (data) => {
    console.log("EVENT RESIZED", data);
    // const { start, end } = data;

    // this.setState((state) => {
    //   state.events[0].start = start;
    //   state.events[0].end = end;
    //   return { events: [...state.events] };
    // });
  };

  const onEventDrop = async (data) => {
    console.log("EVENT DRAGGED AND DROPPED", data);

    let event = { ...data.event };

    // IF MONTH VIEW, JUST CHANGE DAY (KEEP HOURS)
    if (view == Views.MONTH) {
      let draggedDate = dayjs(data.start).tz(DEFAULT_TIMEZONE).startOf("day");
      let newYear = draggedDate.get("year");
      let newMonth = draggedDate.get("month");
      let newDate = draggedDate.get("date");

      // Need to update
      // - event.date
      // - event.startDate
      // - event.endDate

      let startDate = dayjs(event.startDate).tz(DEFAULT_TIMEZONE);
      startDate = startDate
        .set("year", newYear)
        .set("month", newMonth)
        .set("date", newDate);
      let endDate = dayjs(event.endDate).tz(DEFAULT_TIMEZONE);
      endDate = endDate
        .set("year", newYear)
        .set("month", newMonth)
        .set("date", newDate);

      // event.date = draggedDate.toDate().toString();
      // event.startDate = startDate.toDate().toString();
      // event.endDate = endDate.toDate().toString();
      event.date = draggedDate;
      event.startDate = startDate;
      event.endDate = endDate;
      await handleUpdateEvent(event);
    } else {
      // EVERYTHING ELSE, UPDATE THE START/STOP
      let draggedDate = dayjs(data.start).tz(DEFAULT_TIMEZONE).startOf("day");
      let draggedDateEndOfDay = draggedDate.endOf("day");
      let newYear = draggedDate.get("year");
      let newMonth = draggedDate.get("month");
      let newDate = draggedDate.get("date");

      // Need to update
      // - event.date
      // - event.startDate
      // - event.endDate
      let startDate = dayjs(data.start).tz(DEFAULT_TIMEZONE);
      let endDate = dayjs(data.end).tz(DEFAULT_TIMEZONE);

      // END DATE CANNOT EXCEED MIDNINGHT OF CURRENT DAY
      if (endDate.isSameOrAfter(draggedDateEndOfDay)) {
        alert("Event must start and end on the same day");
      } else {
        event.date = draggedDate;
        event.startDate = startDate;
        event.endDate = endDate;
        await handleUpdateEvent(event);
      }
    }
  };

  const getMonth = (date, viewMode) => {
    // // Day Of month is 1-30?
    // const firstDateOfTheMonth = dayjs(date)
    //   .tz(DEFAULT_TIMEZONE)
    //   .startOf("month");
    // const dayOfWeek = dayjs(date).tz(DEFAULT_TIMEZONE).startOf("month").day();

    // // Subtract day of week (1-7) set the first calendar day as the sunday before the first
    // let firstCalendarDate = dayjs(firstDateOfTheMonth)
    //   .tz(DEFAULT_TIMEZONE)
    //   .subtract(dayOfWeek, "day");

    // let dayCount = 0;
    // // Array of 6 weeks (7 days each)
    // const daysMatrix = new Array(6).fill([]).map(() => {
    //   // Fill In Each Week on the inner array
    //   return new Array(7).fill(null).map(() => {
    //     let day = dayjs(firstCalendarDate)
    //       .tz(DEFAULT_TIMEZONE)
    //       .add(dayCount, "day");
    //     dayCount++;
    //     return day;
    //   });
    // });

    // let firstDayOnCalendar = dayjs(daysMatrix[0][0])
    //   .tz(DEFAULT_TIMEZONE)
    //   .subtract(1, "d")
    //   .format("YYYY-MM-DD");
    // let lastDayOnCalendar = dayjs(daysMatrix[5][6])
    //   .tz(DEFAULT_TIMEZONE)
    //   .add(1, "d")
    //   .format("YYYY-MM-DD");

    const currentDate = dayjs(date).tz(DEFAULT_TIMEZONE);

    let firstDayOnCalendar = "";
    let lastDayOnCalendar = "";

    switch (viewMode) {
      case "day":
        // Get all for current day
        firstDayOnCalendar = dayjs(currentDate)
          .subtract(1, "d")
          .format("YYYY-MM-DD");
        lastDayOnCalendar = dayjs(currentDate).add(1, "d").format("YYYY-MM-DD");
        break;
      case "week":
        // Get all for current week
        firstDayOnCalendar = dayjs(currentDate)
          .startOf("isoweek")
          .format("YYYY-MM-DD");
        lastDayOnCalendar = dayjs(currentDate)
          .endOf("isoweek")
          .format("YYYY-MM-DD");
        break;
      default:
        // Month and Linear Month
        const firstDateOfTheMonth = dayjs(currentDate)
          .tz(DEFAULT_TIMEZONE)
          .startOf("month");
        const lastDateOfTheMonth = dayjs(currentDate)
          .tz(DEFAULT_TIMEZONE)
          .endOf("month");

        // Basically we want to add 1 week at the edges of the month because the calendar displays 6 weeks
        firstDayOnCalendar = dayjs(firstDateOfTheMonth)
          .tz(DEFAULT_TIMEZONE)
          .subtract(8, "d")
          .format("YYYY-MM-DD");
        lastDayOnCalendar = dayjs(lastDateOfTheMonth)
          .tz(DEFAULT_TIMEZONE)
          .add(8, "d")
          .format("YYYY-MM-DD");
        break;
    }

    getJobs(
      firstDayOnCalendar,
      lastDayOnCalendar,
      entityParam,
      assignedToParam,
      workStationParam,
    );
    // return daysMatrix;
  };

  const getJobs = async (
    firstDay,
    lastDay,
    entity,
    assignedTo,
    workStation,
  ) => {
    try {
      let queryParams = {
        firstDay,
        lastDay,
        entity,
        assignedTo,
        workStation,
      };
      // console.log(queryParams);
      const response = await ezorder.get(
        `/shop/schedule?${QueryString.stringify(queryParams, {
          arrayFormat: "comma",
        })}`,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        },
      );

      if (response.status == 200) {
        setCalendarEvents(response.data.calendarEvents);
      } else {
      }
    } catch (error) {
      console.log(error);
    } finally {
    }
  };

  const searchManufacturingOrders = async () => {
    try {
      const response = await ezorder.get(`/cfi/manufacturing-orders`, {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });

      if (response.status == 200 || response.status == 201) {
        // setCalendarEvents(response.data.calendarEvents);
        console.log(response.data);
      } else {
      }
    } catch (error) {
      console.log(error);
    } finally {
    }
  };

  const handleScheduleEvents = async (calendarEvents, scheduleFormik) => {
    try {
      const response = await scheduleEvents(calendarEvents);
      if (response.status == 200 || response.status == 201) {
        setScheduleJobDialogData(DEFAULT_SCHEDULE_JOB_DIALOG_DATA);
        scheduleFormik.resetForm();
        getMonth(date, view);
      } else {
        openSnackMessage("error", response.response.data.error);
      }
    } catch (error) {
      console.log(error);
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        console.log(error.response.data);
        console.log(error.response.status);
        console.log(error.response.headers);
        openSnackMessage("error", error.response.data.error);
      } else {
        // Something went really bad
        console.log(error);
        openSnackMessage("error", error);
      }
    } finally {
    }
  };

  const handleUpdateEvent = async (event) => {
    try {
      console.log("UPDATE EVENT", event);
      const response = await updateEvent(event);
      console.log(response);
      setEditJobDialogData(DEFAULT_EDIT_JOB_DIALOG_DATA);
      getMonth(date, view);
    } catch (error) {
    } finally {
    }
  };

  const handleDeleteEvent = async (eventId) => {
    try {
      const response = await deleteEvent(eventId);
      setDeleteJobDialogData(DEFAULT_DELETE_JOB_DIALOG_DATA);
      setScheduleJobDialogData(DEFAULT_SCHEDULE_JOB_DIALOG_DATA);
      getMonth(date, view);
    } catch (error) {
    } finally {
    }
  };

  useEffect(() => {
    if (date) {
      getMonth(date, view);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, view, entityParam, assignedToParam, workStationParam]);

  useEffect(() => {
    if (isoDateParam) {
      setDate(dayjs.tz(isoDateParam, DEFAULT_TIMEZONE).startOf("d"));
    } else {
      setDate(dayjs().tz(DEFAULT_TIMEZONE).startOf("d"));
    }
  }, [isoDateParam]);

  useEffect(() => {
    if (viewParam) setView(viewParam);
    else {
      setView(
        isSmallScreen
          ? Views.DAY
          : decoded.role === "EZORDER_ADMIN"
            ? Views.MONTH
            : Views.DAY,
      );
    }
  }, [viewParam]);

  useEffect(() => {
    searchManufacturingOrders();
  }, []);

  useEffect(() => {
    // Set initial height on mount
    handleResize();

    // Add event listener for window resize
    window.addEventListener("resize", handleResize);

    // Clean up event listener on component unmount
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    if (
      // IF SHOP ROLE AND URL PARAMS ARE EMPTY (localhost:3000 or localhost:3000/shop-schedule)
      // ADMIN AND FOREMAN DONT DEFAULT TO THEIR OWN ASSIGNMENTS
      decoded &&
      decoded.role == "EZORDER_EMPLOYEE" &&
      isoDateParam === null &&
      entityParam === null &&
      assignedToParam === null &&
      workStationParam === null
    ) {
      navigate(`/shop-schedule?assignedTo=${decoded.id}`);
    }
  }, [
    decoded,
    isoDateParam,
    viewParam,
    entityParam,
    assignedToParam,
    workStationParam,
  ]);

  if (date && view && calendarEvents)
    return (
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: isMediumScreen ? "column" : "row",

          // This is to offset the Dashboard container extra margins
          marginLeft: isMediumScreen ? 0 : -3, // Offset margin from Dashboard Container
          marginRight: 0, // Offset margin from Dashboard Container
          paddingTop: isMediumScreen ? 0 : `${PADDING_TOP}px`,
          marginTop: isMediumScreen ? 0 : -4,
          marginBottom: isMediumScreen ? 0 : -40,
          // paddingTop: "64px",
        }}
      >
        <Card
          sx={{
            // width: isMediumScreen ? "100%" : "270px",
            width: isMediumScreen ? "100%" : "300px",
            height: isMediumScreen ? "inherit" : `${calendarHeight}px`, // IMPORTANT: this needs to be equal to .rbc-time-header height in global.css
            overflowY: "scroll",
          }}
        >
          <Box sx={{}}>
            <CalendarToolBar
              view={view}
              date={date}
              isAdmin={
                decoded.role == "EZORDER_ADMIN" ||
                decoded.role == "EZORDER_SHOP_ACCOUNT"
                  ? true
                  : false
              }
              onNavigate={onNavigate}
              handleOnChangeView={handleOnChangeView}
              entity={entityParam}
              handleEntityFilter={handleEntityFilter}
              assignedTo={assignedToParam}
              handleAssignedToFilter={handleAssignedToFilter}
              workStation={workStationParam}
              handleWorkStationFilter={handleWorkStationFilter}
            />
          </Box>
        </Card>

        <Card
          style={{
            flex: 1,
            border: "none",
            boxShadow: "none",
            height: isMediumScreen ? "inherit" : `${calendarHeight}px`, // IMPORTANT: this needs to be equal to .rbc-t
            overflowY: "scroll",
          }}
        >
          {localizer && (
            // <Calendar
            <DnDCalendar
              // onDropFromOutside={}
              resizableAccessor={(event) => false} // Dont allow extend/shrink event
              // Allow drag and drop for ADMIN ONLY
              draggableAccessor={(event) => decoded.role === "EZORDER_ADMIN"}
              onEventDrop={onEventDrop}
              onEventResize={onEventResize}
              date={date}
              onNavigate={onNavigate} //Callback fired when the date value changes. When included it is used to 'control' the date prop value.
              events={calendarEvents}
              toolbar={false}
              // <DnDCalendar

              selectable
              onSelectSlot={(slotInfo) => {
                // alert("Slot selected")
              }}
              // longPressThreshold={0} // React Calendar does not support touch events for mobile currently.
              // So make this longpress as short as possible to trigger onSelectSlot on mobile
              onSelectEvent={(calendarEvent) => {}}
              // toolbar={false}
              view={view}
              defaultView={Views.MONTH}
              // views={["month", "week", "day"]}
              views={{
                // agenda: true,
                day: true,
                // week: isSmallScreen ? CustomWeekView : true,
                week: CustomWeekView,
                month: true,
                linear_month: CustomMonthView,
                two_weeks: CustomWeekView,
              }}
              onView={handleOnChangeView}
              startAccessor={(event) => {
                // https://stackoverflow.com/questions/57287782/typeerror-dateget-method-is-not-a-function-in-react-big-calendar
                return new Date(event.startDate);
              }}
              endAccessor={(event) => {
                // https://stackoverflow.com/questions/57287782/typeerror-dateget-method-is-not-a-function-in-react-big-calendar
                return new Date(event.endDate);
              }}
              allDayAccessor={(event) => {
                // If true, then the event is shown in the all-day view (as opposed to time view)
                // return true;
                return false; // if false, then the event is shown only in time view
              }}
              // resizableAccessor={(event) => !isSmallScreen}
              // draggableAccessor={(event) => !isSmallScreen}

              localizer={localizer}
              style={{
                // minHeight: view == Views.MONTH ? `${calendarHeight}px` : "inherit", // IMPORTANT: this needs to be equal to .rbc-time-header height in global.css
                // height: `${calendarHeight}px`,
                minHeight: "300px",
                height: isMediumScreen ? "inherit" : `${calendarHeight}px`, // IMPORTANT: this needs to be equal to .rbc-t
              }}
              eventPropGetter={(event) => {
                // Style Event
                // const backgroundColor = getAvatarColor(event.assignedTo.id);
                // let backgroundColor = "dodgerblue";

                // RFL Jobs don't have phases and subphases?
                // if (event.subPhases && event.subPhases.length > 0) {
                //   let areAllEventTasksComplete = true;

                //   for (let subPhase of event.subPhases) {
                //     if (subPhase.progress != 100) {
                //       areAllEventTasksComplete = false;
                //       break;
                //     }
                //   }

                //   if (areAllEventTasksComplete) {
                //     backgroundColor = "lightgray";
                //   }
                // }
                let backgroundColor;
                if (
                  event.order &&
                  event.order.shopCompletionStatus == "100% Complete"
                ) {
                  // If job is complete, grey out task
                  backgroundColor = "lightgray";
                } else {
                  // If job is not complete, get workstation color
                  // Default background color to station color
                  backgroundColor = getWorkStationColor(
                    event.workStation
                      ? event.workStation.workStationType
                      : null,
                  );
                }

                return { style: { backgroundColor } };
              }}
              popup={true}
              showAllEvents={false} // allow scroll on calendar rows
              messages={{
                showMore: (target) => (
                  <Box
                    className="ml-2"
                    role="presentation"
                    onClick={() =>
                      this.setState({
                        calendarOverlay: true,
                        currentTitleData: {},
                      })
                    }
                  >
                    <Typography
                      sx={{
                        overflow: "hidden",
                        fontSize: "12px",
                        fontWeight: "bold",
                      }}
                    >
                      ...{target} more
                    </Typography>
                  </Box>
                ),
              }}
              components={{
                eventWrapper: (props) => {
                  return (
                    <div
                      style={{
                        marginBottom: "1px",
                        // background: getAvatarColor(props.event.assignedTo.id),
                        // background: "blue",
                      }}
                    >
                      {props.children}
                    </div>
                  );
                },
                month: {
                  dateHeader: ({ date, label }) => {
                    return (
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "row",
                          alignItems: "center",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "row",
                            justifyContent: "flex-start",
                          }}
                        >
                          {decoded.role == "EZORDER_ADMIN" && (
                            <IconButton
                              onClick={() => {
                                setScheduleJobDialogData({
                                  open: true,
                                  calendarEvent: {
                                    date: dayjs(date)
                                      .tz(DEFAULT_TIMEZONE)
                                      .startOf("day"),
                                    startDate: dayjs(date)
                                      .tz(DEFAULT_TIMEZONE)
                                      .startOf("day")
                                      .add(7, "hours"),
                                    endDate: dayjs(date)
                                      .tz(DEFAULT_TIMEZONE)
                                      .startOf("day")
                                      .add(8, "hours"),
                                  },
                                });
                              }}
                              color="primary"
                            >
                              <Add
                                sx={{
                                  fontSize: isSmallScreen ? "16px" : "20px",
                                }}
                              />
                            </IconButton>
                          )}
                        </Box>
                        <Typography
                          sx={{
                            flex: 1,
                            fontSize: isSmallScreen ? "12px" : "16px",
                            fontWeight: "bold",
                            cursor: "pointer",
                            "&:hover": {
                              color: `${theme.palette.primary.main} !important`,
                            },
                          }}
                          onClick={() => {
                            handleDaySelect(date);
                          }}
                        >
                          {label}
                        </Typography>
                      </Box>
                    );
                  },
                  event: (props) => {
                    let {
                      slotStart,
                      slotEnd,
                      continuesAfter,
                      continuesPrior,
                      event,
                    } = props;
                    return (
                      <ShopCalendarEvent
                        calendarEvent={event}
                        onClick={() => {
                          setJobDialogData({
                            open: true,
                            calendarEvent: event,
                          });
                        }}
                      />
                    );
                  },
                },
                linear_month: {
                  header: ({ date, label }) => {
                    return (
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "row",
                          alignItems: "center",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "row",
                            justifyContent: "flex-start",
                          }}
                        >
                          {decoded.role == "EZORDER_ADMIN" && (
                            <IconButton
                              onClick={() => {
                                setScheduleJobDialogData({
                                  open: true,
                                  calendarEvent: {
                                    date: dayjs(date)
                                      .tz(DEFAULT_TIMEZONE)
                                      .startOf("day"),
                                    startDate: dayjs(date)
                                      .tz(DEFAULT_TIMEZONE)
                                      .startOf("day")
                                      .add(7, "hours"),
                                    endDate: dayjs(date)
                                      .tz(DEFAULT_TIMEZONE)
                                      .startOf("day")
                                      .add(8, "hours"),
                                  },
                                });
                              }}
                              color="primary"
                            >
                              <Add
                                sx={{
                                  fontSize: isSmallScreen ? "16px" : "20px",
                                }}
                              />
                            </IconButton>
                          )}
                        </Box>
                        <Typography
                          sx={{
                            flex: 1,
                            fontSize: isSmallScreen ? "12px" : "16px",
                            fontWeight: "bold",
                            cursor: "pointer",
                            "&:hover": {
                              color: `${theme.palette.primary.main} !important`,
                            },
                          }}
                          onClick={() => {
                            // UI ALREADY CALLS onNavigate when header is clicked
                            // setDate(new Date(date));
                            // handleOnChangeView("day");
                            // handleDaySelect(date);
                          }}
                        >
                          {label}
                        </Typography>
                      </Box>
                    );
                  },
                  event: (props) => {
                    let {
                      slotStart,
                      slotEnd,
                      continuesAfter,
                      continuesPrior,
                      event,
                    } = props;
                    return (
                      <ShopCalendarEvent
                        calendarEvent={event}
                        onClick={() => {
                          setJobDialogData({
                            open: true,
                            calendarEvent: event,
                          });
                        }}
                      />
                    );
                  },
                },
                week: {
                  header: ({ date, label, ...props }) => {
                    return (
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "row",
                          alignItems: "center",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "row",
                            justifyContent: "flex-start",
                          }}
                        >
                          {decoded.role == "EZORDER_ADMIN" && (
                            <IconButton
                              onClick={() => {
                                setScheduleJobDialogData({
                                  open: true,
                                  calendarEvent: {
                                    date: dayjs(date)
                                      .tz(DEFAULT_TIMEZONE)
                                      .startOf("day"),
                                    startDate: dayjs(date)
                                      .tz(DEFAULT_TIMEZONE)
                                      .startOf("day")
                                      .add(7, "hours"),
                                    endDate: dayjs(date)
                                      .tz(DEFAULT_TIMEZONE)
                                      .startOf("day")
                                      .add(8, "hours"),
                                  },
                                });
                              }}
                              color="primary"
                            >
                              <Add
                                sx={{
                                  fontSize: isSmallScreen ? "16px" : "20px",
                                }}
                              />
                            </IconButton>
                          )}
                        </Box>
                        <Typography
                          sx={{
                            flex: 1,
                            fontSize: isSmallScreen ? "12px" : "16px",
                            fontWeight: "bold",
                            cursor: "pointer",
                            "&:hover": {
                              color: `${theme.palette.primary.main} !important`,
                            },
                          }}
                          onClick={() => {
                            // UI ALREADY CALLS onNavigate when header is clicked
                            // setDate(new Date(date));
                            // handleOnChangeView("day");
                            // handleDaySelect(date);
                          }}
                        >
                          {label}
                        </Typography>
                      </Box>
                    );
                  },
                  event: (props) => {
                    let {
                      slotStart,
                      slotEnd,
                      continuesAfter,
                      continuesPrior,
                      event,
                    } = props;
                    return (
                      <ShopCalendarEvent
                        calendarEvent={event}
                        onClick={() => {
                          setJobDialogData({
                            open: true,
                            calendarEvent: event,
                          });
                        }}
                      />
                    );
                  },
                },
                day: {
                  event: (props) => {
                    let {
                      slotStart,
                      slotEnd,
                      continuesAfter,
                      continuesPrior,
                      event,
                    } = props;
                    return (
                      <ShopCalendarEvent
                        calendarEvent={event}
                        onClick={() => {
                          setJobDialogData({
                            open: true,
                            calendarEvent: event,
                          });
                        }}
                      />
                    );
                  },
                },
              }}
            />
          )}

          {decoded.role == "EZORDER_ADMIN" && (
            <EditJobDialog
              dialogData={editJobDialogData}
              view={view}
              handleClose={() => {
                setEditJobDialogData(DEFAULT_EDIT_JOB_DIALOG_DATA);
              }}
              handleSubmit={(event) => {
                handleUpdateEvent(event);
              }}
            />
          )}

          {decoded.role == "EZORDER_ADMIN" && (
            <DeleteJobDialog
              dialogData={deleteJobDialogData}
              view={view}
              handleClose={() => {
                setDeleteJobDialogData(DEFAULT_DELETE_JOB_DIALOG_DATA);
              }}
              handleSubmit={(eventId) => {
                handleDeleteEvent(eventId);
              }}
            />
          )}

          <JobDialog
            dialogData={jobDialogData}
            view={view}
            handleClose={() => {
              setJobDialogData(DEFAULT_JOB_DIALOG_DATA);
            }}
            handleEdit={(calendarEvent) => {
              setEditJobDialogData({
                open: true,
                calendarEvent: {
                  ...calendarEvent,
                  // Convert iso 6801 string from db to dayjs object
                  date: dayjs(calendarEvent.date).tz(DEFAULT_TIMEZONE),
                  startDate: dayjs(calendarEvent.startDate).tz(
                    DEFAULT_TIMEZONE,
                  ),
                  endDate: dayjs(calendarEvent.endDate).tz(DEFAULT_TIMEZONE),
                },
              });

              setJobDialogData(DEFAULT_JOB_DIALOG_DATA);
            }}
            handleCopyUnfinished={(calendarEvent) => {
              setJobDialogData(DEFAULT_JOB_DIALOG_DATA);
              // const DEFAULT_SCHEDULE_JOB_DIALOG_DATA = {
              //   open: false,
              //   date: null,
              //   calendarEvent: null,
              // };

              // Remove all calendar.subPhases that are 100%
              let eventCopy = { ...calendarEvent };
              let unfinishedSubPhases = eventCopy.subPhases
                ? eventCopy.subPhases.filter(
                    (subPhase) => subPhase.progress < 100,
                  )
                : null;

              console.log("Reschedule event", calendarEvent);

              // setEditJobDialogData({
              setScheduleJobDialogData({
                open: true,
                calendarEvent: {
                  ...calendarEvent,
                  id: null,
                  subPhases: unfinishedSubPhases,

                  // Convert iso 6801 string from db to dayjs object
                  date: dayjs(calendarEvent.date).tz(DEFAULT_TIMEZONE),
                  startDate: dayjs(calendarEvent.startDate).tz(
                    DEFAULT_TIMEZONE,
                  ),
                  endDate: dayjs(calendarEvent.endDate).tz(DEFAULT_TIMEZONE),
                },
                isReScheduling: true,
              });

              setJobDialogData(DEFAULT_JOB_DIALOG_DATA);
            }}
            handleDelete={() => {
              setDeleteJobDialogData({
                open: true,
                calendarEvent: jobDialogData.calendarEvent,
              });
              setJobDialogData(DEFAULT_JOB_DIALOG_DATA);
            }}
          />
          <ScheduleJobEventsDialog
            dialogData={scheduleJobDialogData}
            view={view}
            handleClose={() => {
              console.log("CLOSED");
              setScheduleJobDialogData(DEFAULT_SCHEDULE_JOB_DIALOG_DATA);
            }}
            handleSubmit={(scheduleData, scheduleFormik) => {
              handleScheduleEvents(scheduleData.calendarEvents, scheduleFormik);
            }}
            handleDelete={() => {
              setDeleteJobDialogData({
                open: true,
                calendarEvent: scheduleJobDialogData.calendarEvent,
              });
            }}
          />
          {decoded.role == "EZORDER_ADMIN" && (
            <ScheduleButton
              onAdding={() => {
                setScheduleJobDialogData({
                  open: true,
                  calendarEvent: {
                    date: dayjs(date).tz(DEFAULT_TIMEZONE).startOf("day"),
                    startDate: dayjs(date)
                      .tz(DEFAULT_TIMEZONE)
                      .startOf("day")
                      .add(7, "hours"),
                    endDate: dayjs(date)
                      .tz(DEFAULT_TIMEZONE)
                      .startOf("day")
                      .add(8, "hours"),
                  },
                });
              }}
            />
          )}
        </Card>
      </Box>
    );
  else return <Typography>Loading Calendar</Typography>;
};

export default ShopCalendar;
